import { useCallback, useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { SelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/SelectCombobox";
import { renderCheckboxOption } from "app/pages/sales-intelligence/common-components/dropdown/utils/renderCheckboxOption";
import { FilterSuggestionsBar } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterSuggestionsBar";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type IabCategoriesFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/IabCategoriesFilter/IabCategoriesFilter";
import type { IIabCategoryItemDescriptionDto } from "app/pages/sales-intelligence/modules/find-apps-filters/types/description.dto";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

import IabCategoriesInclusion from "./components/IabCategoriesInclusion/IabCategoriesInclusion";
import useIabCategoriesFilter from "./hooks/useIabCategoriesSuggestions";
import type { IIabCategoryDropdownItem } from "./types";

const IabCategoriesFilterView = ({ filter, value, onFilterUpdate }: IFilterViewBaseProps<IabCategoriesFilter>) => {
    const [localInclusionValue, setLocalInclusionValue] = useState<InclusionOption>(inclusionOptionMap.includeOnly);

    const categories = useMemo((): IIabCategoryDropdownItem[] => {
        if (!filter.options) {
            return [];
        }

        return filter.options.map((option) => ({
            ...option,
            text: option.name,
        }));
    }, [filter.options]);

    const handleInclusionChange = (inclusion: InclusionOption) => {
        setLocalInclusionValue(inclusion);

        if (!value?.values?.length) {
            return;
        }

        onFilterUpdate({
            ...value,
            inclusion,
        });
    };

    const handleSelect = useCallback(
        (category?: IIabCategoryItemDescriptionDto) => {
            if (!category) {
                return;
            }

            const values = value?.values || [];

            onFilterUpdate({
                values: values.includes(category.id)
                    ? values.filter((id) => id !== category.id)
                    : values.concat(category.id),
                inclusion: localInclusionValue,
            });
        },
        [value?.values, localInclusionValue],
    );

    const { isFilterDirty, isLoading, suggestedCategoryChips } = useIabCategoriesFilter({
        value,
        filter,
        handleSelect,
    });

    return (
        <Stack gap="16px">
            <IabCategoriesInclusion
                value={localInclusionValue}
                onChange={handleInclusionChange}
            />

            <div>
                <FilterSuggestionsBar
                    suggestedItems={suggestedCategoryChips}
                    isEnabled={isFilterDirty}
                    isLoading={isLoading}
                />

                <Stack
                    width="260px"
                    gap="8px"
                >
                    <Text
                        fontVariant="sectionTitle"
                        color={colorsPalettes.carbon[400]}
                    >
                        {translate("common.category")}
                    </Text>

                    <SelectCombobox<IIabCategoryDropdownItem>
                        items={categories}
                        isItemSelected={(item) => (value?.values || []).includes(item.id)}
                        onSelect={handleSelect}
                        buttonText={translate("si.find_apps.filters.iab-categories.dd-button-text")}
                        width={260}
                        hasSearch
                        searchPlaceHolder={translate("si.find_apps.filters.iab-categories.dd-placeholder")}
                        renderOption={renderCheckboxOption}
                    />
                </Stack>
            </div>
        </Stack>
    );
};

export default IabCategoriesFilterView;
